import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import {
  remediationService,
  type RemediationPlanSummary,
  type RemediationPlanStatus,
} from '@services/remediationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';
import { formatDate, formatDateTime as formatDateTime0 } from '@utils/format';

const STATUS_COLORS: Record<RemediationPlanStatus, 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  active: 'info',
  completed: 'success',
  archived: 'warning',
};

/** Format a timestamp, returning an em dash for missing/unparseable values. */
function formatDateTime(value: string | null | undefined): string {
  return formatDateTime0(value);
}

/**
 * REV-214: lists remediation plans for a client organization. Routes on
 * /orgs/:slug/remediation. Every org member sees every plan; members and up
 * can create one, viewers cannot (enforced server-side, REV-895).
 */
export default function RemediationPlansPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [plans, setPlans] = useState<RemediationPlanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      const rows = await remediationService.listPlans(org.id);
      setPlans(rows);
    } catch (err) {
      setError(
        toDisplayError(err, t('remediation.errors.fetchFailed', 'Failed to load remediation plans'))
      );
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!orgId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const plan = await remediationService.createPlan(orgId, {
        name: name.trim(),
        description: description.trim() || null,
      });
      setDialogOpen(false);
      setName('');
      setDescription('');
      void navigate(`/orgs/${slug}/remediation/${plan.id}`);
    } catch (err) {
      setCreateError(
        toDisplayError(err, t('remediation.errors.createFailed', 'Failed to create plan'))
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('remediation.title', 'Remediation Roadmap')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={!orgId}
        >
          {t('remediation.newPlan', 'New plan')}
        </Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'remediation.subtitle',
          'Prioritized remediation work across every tracked place in this organization.'
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ScrollableTableContainer component={Paper} variant="outlined">
        <Table aria-label={t('remediation.tableLabel', 'Remediation plans')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('remediation.columns.name', 'Name')}</TableCell>
              <TableCell>{t('remediation.columns.status', 'Status')}</TableCell>
              <TableCell>{t('remediation.columns.targetDate', 'Target date')}</TableCell>
              <TableCell>{t('remediation.columns.updated', 'Updated')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && plans.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    {t(
                      'remediation.empty',
                      'No remediation plans yet. Create one to start tracking priorities.'
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {/* REV-732: no onClick on the row.

                A <tr> carrying a click handler is the same defect as a
                <div> carrying one — it is not focusable, not announced as
                anything, and not reachable by keyboard, so row-click
                navigation existed for pointer users only. A row cannot be
                an anchor either: wrapping <tr> in <a> is invalid HTML.

                The accessible route was already here — the plan name in
                the first cell is a real link, which is why that link had
                to call stopPropagation to stop the row navigating a second
                time. Removing the handler removes the duplicate and the
                workaround with it.

                Mouse users lose whole-row clicking. That is a deliberate
                trade: it was never available to anyone else, and the link
                it duplicated is still there. */}
            {!loading &&
              plans.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/orgs/${slug}/remediation/${p.id}`}
                      variant="body2"
                      fontWeight={500}
                      underline="hover"
                    >
                      {p.name}
                    </Link>
                    {p.description && (
                      <Typography variant="caption" color="text.secondary">
                        {p.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLORS[p.status]}
                      label={t(`remediation.statuses.${p.status}`, p.status)}
                    />
                  </TableCell>
                  <TableCell>{p.target_date ? formatDate(p.target_date) : '—'}</TableCell>
                  <TableCell>{formatDateTime(p.updatedAt)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </ScrollableTableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('remediation.createDialog.title', 'New remediation plan')}</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('remediation.createDialog.name', 'Name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              // autoFocus is correct for a modal dialog: the user just
              // opened the dialog intending to type here.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputProps={{ maxLength: 255 }}
              fullWidth
            />
            <TextField
              label={t('remediation.createDialog.description', 'Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || name.trim().length === 0}
          >
            {creating
              ? t('common.creating', 'Creating…')
              : t('remediation.createDialog.submit', 'Create plan')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
